import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useDropzone } from 'react-dropzone';
import {
  fetchKnowledgeCenter,
  unlinkDocumentFromProject, unlinkFolderFromProject,
  fetchDocuments, fetchFolderTree, bulkLinkToProject,
  clearKnowledgeCenter, createKcFolder, renameFolder, updateDocument, deleteDocument,
  linkDocumentToProject,
} from '../../store/slices/librarySlice';
import { shouldUseChunkedUpload } from '../../utils/chunkedUploader';
import ConfirmModal from '../../components/common/ConfirmModal';
import Modal from '../../components/common/Modal';
import FileExplorer from '../../components/library/FileExplorer';
import { useUploadManager } from '../../contexts/UploadManagerContext';
import './KnowledgeCenter.css';

const KnowledgeCenter = ({ projectId, members, canManage }) => {
  const dispatch = useDispatch();
  const { addUpload, downloadFile } = useUploadManager();
  const { knowledgeCenter, folderTree } = useSelector((state) => state.library);
  const { documents, folders, totalDocuments, totalFolders, loading, breadcrumbs, currentFolderId } = knowledgeCenter;

  const [search, setSearch] = useState('');
  const [currentFolder, setCurrentFolder] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkDocuments, setLinkDocuments] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [selectedFolderIds, setSelectedFolderIds] = useState([]);
  const searchTimerRef = useRef(null);

  // Create folder
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  // Rename
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [renaming, setRenaming] = useState(false);

  // Context menu
  const [contextMenu, setContextMenu] = useState(null);
  const contextMenuRef = useRef(null);

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false, title: '', message: '', confirmText: 'Unlink',
    variant: 'danger', onConfirm: null, isLoading: false,
  });

  // Close context menu on outside click, scroll, or resize
  useEffect(() => {
    const onMouseDown = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(null);
      }
    };
    const onClose = () => setContextMenu(null);
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, []);

  // Load knowledge center data
  const loadData = useCallback(() => {
    dispatch(fetchKnowledgeCenter({ projectId, search, folderId: currentFolder }));
  }, [dispatch, projectId, search, currentFolder]);

  useEffect(() => {
    loadData();
    return () => { dispatch(clearKnowledgeCenter()); };
  }, [loadData, dispatch]);

  // Debounced search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearch(value), 300);
  };

  // Folder navigation
  const navigateToFolder = (folderId) => { setCurrentFolder(folderId); setSearch(''); };
  const navigateBack = () => {
    if (breadcrumbs && breadcrumbs.length > 0) {
      const parent = breadcrumbs[breadcrumbs.length - 2];
      setCurrentFolder(parent ? parent.id : null);
    } else {
      setCurrentFolder(null);
    }
  };
  const navigateToRoot = () => { setCurrentFolder(null); setSearch(''); };

  // Create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreatingFolder(true);
    try {
      const newFolder = await dispatch(createKcFolder({
        projectId: parseInt(projectId),
        name: newFolderName.trim(),
        parentFolderId: currentFolder || null,
      })).unwrap();
      toast.success(`Folder "${newFolderName.trim()}" created`);
      setShowCreateFolderModal(false);
      setNewFolderName('');
      loadData();
    } catch (err) {
      toast.error(err || 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  // Rename
  const handleRename = async () => {
    if (!renameName.trim() || !renameTarget) return;
    setRenaming(true);
    try {
      if (renameTarget.type === 'folder') {
        await dispatch(renameFolder({ folderId: renameTarget.id, name: renameName.trim() })).unwrap();
      } else {
        await dispatch(updateDocument({ documentId: renameTarget.id, data: { original_filename: renameName.trim() } })).unwrap();
      }
      toast.success('Renamed successfully');
      setShowRenameModal(false);
      setRenameTarget(null);
      loadData();
    } catch (err) {
      toast.error(err || 'Failed to rename');
    } finally {
      setRenaming(false);
    }
  };

  // Context menu — right-click
  const handleContextMenu = (e, item, type) => {
    e.preventDefault();
    // Non-managers have nothing useful on folders
    if (type === 'folder' && !canManage) return;
    const menuW = 192;
    const menuH = type === 'document' ? (canManage ? 240 : 110) : 120;
    const x = Math.min(e.clientX, window.innerWidth - menuW - 8);
    const y = Math.min(e.clientY, window.innerHeight - menuH - 8);
    setContextMenu({ x, y, item: { ...item, type } });
  };

  // Context menu — three-dot button on folder row
  const handleFolderAction = useCallback((action, folder, btnEl) => {
    if (action === 'menu' && btnEl) {
      const rect = btnEl.getBoundingClientRect();
      const menuW = 192;
      const menuH = 120;
      const x = Math.max(4, Math.min(rect.right - menuW, window.innerWidth - menuW - 4));
      const spaceBelow = window.innerHeight - rect.bottom;
      const y = spaceBelow >= menuH + 4
        ? rect.bottom + 4
        : Math.max(4, rect.top - menuH - 4);
      setContextMenu({ x, y, item: { ...folder, type: 'folder' } });
    }
  }, []);

  // Context menu — three-dot button on file row
  const handleFileAction = useCallback((action, doc, btnEl) => {
    if (action === 'menu' && btnEl) {
      const rect = btnEl.getBoundingClientRect();
      const menuW = 192;
      const menuH = canManage ? 240 : 110;
      const x = Math.max(4, Math.min(rect.right - menuW, window.innerWidth - menuW - 4));
      const spaceBelow = window.innerHeight - rect.bottom;
      const y = spaceBelow >= menuH + 4
        ? rect.bottom + 4
        : Math.max(4, rect.top - menuH - 4);
      setContextMenu({ x, y, item: { ...doc, type: 'document' } });
    }
  }, [canManage]);

  // Upload
  const onDrop = useCallback((acceptedFiles) => {
    for (const file of acceptedFiles) {
      const params = new URLSearchParams();
      if (currentFolder) params.append('folder_id', currentFolder);
      const isChunked = shouldUseChunkedUpload(file.size);
      addUpload(file, {
        folderId: currentFolder,
        uploadUrl: `/projects/${projectId}/knowledge/upload?${params.toString()}`,
        onSuccess: async (document) => {
          // Chunked uploads go to /library/upload/complete which doesn't link the
          // document to the project — do it here after the fact.
          if (isChunked && document?.id) {
            try {
              await dispatch(linkDocumentToProject({ documentId: document.id, projectId: parseInt(projectId) }));
            } catch (e) {
              toast.warning(`File uploaded to Library but could not link to this project`);
            }
          }
          loadData();
        },
        onError: (error) => {
          toast.error(error?.response?.data?.detail || error.message || `Failed to upload "${file.name}"`);
        },
      });
    }
  }, [addUpload, dispatch, projectId, currentFolder, loadData]);

  const { getInputProps, open: openFilePicker } = useDropzone({ onDrop, noClick: true, noKeyboard: true });

  // Unlink document
  const handleUnlinkDocument = (doc) => {
    setContextMenu(null);
    setConfirmModal({
      isOpen: true,
      title: 'Unlink Document',
      message: `Remove "${doc.original_filename}" from this project's Knowledge Center? The file will remain in the Library.`,
      confirmText: 'Unlink',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await dispatch(unlinkDocumentFromProject({ documentId: doc.id, projectId: parseInt(projectId) })).unwrap();
          toast.success('Document unlinked');
          loadData();
        } catch (err) { toast.error(err || 'Failed to unlink document'); }
        setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
      },
    });
  };

  // Unlink folder
  const handleUnlinkFolder = (folder) => {
    setContextMenu(null);
    setConfirmModal({
      isOpen: true,
      title: 'Unlink Folder',
      message: `Remove "${folder.name}" from this project's Knowledge Center? The folder will remain in the Library.`,
      confirmText: 'Unlink',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await dispatch(unlinkFolderFromProject({ folderId: folder.id, projectId: parseInt(projectId) })).unwrap();
          toast.success('Folder unlinked');
          loadData();
        } catch (err) { toast.error(err || 'Failed to unlink folder'); }
        setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
      },
    });
  };

  // Delete document from library entirely
  const handleDeleteDocument = (doc) => {
    setContextMenu(null);
    setConfirmModal({
      isOpen: true,
      title: 'Delete File',
      message: `Permanently delete "${doc.original_filename}"? This will remove the file from the Library and all projects it is linked to.`,
      confirmText: 'Delete',
      variant: 'danger',
      isLoading: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        try {
          await dispatch(deleteDocument(doc.id)).unwrap();
          toast.success('File deleted');
          loadData();
        } catch (err) { toast.error(err || 'Failed to delete file'); }
        setConfirmModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
      },
    });
  };

  // Link existing
  const openLinkModal = async () => {
    setShowLinkModal(true);
    setLinkSearch('');
    setSelectedDocIds([]);
    setSelectedFolderIds([]);
    setLinkLoading(true);
    try {
      const result = await dispatch(fetchDocuments({ allFolders: true, pageSize: 50 })).unwrap();
      setLinkDocuments(result.items || []);
      dispatch(fetchFolderTree());
    } catch (err) { toast.error('Failed to load library'); }
    setLinkLoading(false);
  };

  const handleLinkSearch = async (value) => {
    setLinkSearch(value);
    setLinkLoading(true);
    try {
      const result = await dispatch(fetchDocuments({ search: value, allFolders: true, pageSize: 50 })).unwrap();
      setLinkDocuments(result.items || []);
    } catch (err) {}
    setLinkLoading(false);
  };

  const toggleDocSelection = (docId) => setSelectedDocIds(prev => prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]);
  const toggleFolderSelection = (folderId) => setSelectedFolderIds(prev => prev.includes(folderId) ? prev.filter(id => id !== folderId) : [...prev, folderId]);

  const handleBulkLink = async () => {
    if (selectedDocIds.length === 0 && selectedFolderIds.length === 0) return;
    try {
      await dispatch(bulkLinkToProject({ documentIds: selectedDocIds, folderIds: selectedFolderIds, projectId: parseInt(projectId) })).unwrap();
      toast.success('Items linked to project');
      setShowLinkModal(false);
      loadData();
    } catch (err) { toast.error(err || 'Failed to link items'); }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return '🖼';
    if (contentType?.startsWith('video/')) return '🎬';
    if (contentType?.startsWith('audio/')) return '🎵';
    if (contentType === 'application/pdf') return '📄';
    if (contentType?.includes('word') || contentType?.includes('document')) return '📝';
    if (contentType?.includes('sheet') || contentType?.includes('excel')) return '📊';
    if (contentType?.includes('presentation') || contentType?.includes('powerpoint')) return '📑';
    return '📎';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const linkedDocIds = new Set(documents.map(d => d.id));
  const linkedFolderIds = new Set(folders.map(f => f.id));

  return (
    <div className="knowledge-center">
      <input {...getInputProps()} style={{ display: 'none' }} />
      {/* Header */}
      <div className="kc-header">
        <div className="kc-header-left">
          <div className="kc-search">
            <svg className="kc-search-icon" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path fillRule="evenodd" d="M11.742 10.344a6.5 6.5 0 10-1.397 1.398h-.001l3.85 3.85a1 1 0 001.415-1.414l-3.85-3.85-.017.016zm-5.442.656a5 5 0 110-10 5 5 0 010 10z" clipRule="evenodd" />
            </svg>
            <input type="text" placeholder="Search documents..." onChange={handleSearchChange} className="kc-search-input" />
          </div>
          <span className="kc-count">
            {totalDocuments} document{totalDocuments !== 1 ? 's' : ''}
            {totalFolders > 0 && `, ${totalFolders} folder${totalFolders !== 1 ? 's' : ''}`}
          </span>
        </div>
        {canManage && (
          <div className="kc-header-right">
            <button className="btn btn-secondary btn-sm" onClick={openLinkModal}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M4.715 6.542L3.343 7.914a3 3 0 104.243 4.243l1.828-1.829A3 3 0 008.586 5.5L8 6.086a1.002 1.002 0 00-.154.199 2 2 0 01.861 3.337L6.88 11.45a2 2 0 11-2.83-2.83l.793-.792a4.018 4.018 0 01-.128-1.287z" />
                <path d="M6.586 4.672A3 3 0 007.414 9.5l.775-.776a2 2 0 01-.896-3.346L9.12 3.55a2 2 0 112.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 10-4.243-4.243L6.586 4.672z" />
              </svg>
              Link Existing
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setNewFolderName(''); setShowCreateFolderModal(true); }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M.5 3l.04.87a1.99 1.99 0 00-.342 1.311l.637 7A2 2 0 002.826 14h10.348a2 2 0 001.991-1.819l.637-7A2 2 0 0013.81 3H9.828a2 2 0 01-1.414-.586l-.828-.828A2 2 0 006.172 1H2.5a2 2 0 00-2 2z" />
              </svg>
              New Folder
            </button>
            <button className="btn btn-primary btn-sm" onClick={openFilePicker}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5z" />
                <path d="M7.646 1.146a.5.5 0 01.708 0l3 3a.5.5 0 01-.708.708L8.5 2.707V11.5a.5.5 0 01-1 0V2.707L5.354 4.854a.5.5 0 11-.708-.708l3-3z" />
              </svg>
              Upload
            </button>
          </div>
        )}
      </div>

      {/* Breadcrumbs */}
      {((breadcrumbs && breadcrumbs.length > 0) || currentFolder) ? (
        <div className="kc-breadcrumbs">
          <button className="kc-back-btn" onClick={navigateBack} title="Go back">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 010 .708L5.707 8l5.647 5.646a.5.5 0 01-.708.708l-6-6a.5.5 0 010-.708l6-6a.5.5 0 01.708 0z" clipRule="evenodd" />
            </svg>
          </button>
          <div className="kc-breadcrumb-path">
            <button className="kc-breadcrumb-item" onClick={navigateToRoot}>Knowledge Center</button>
            {breadcrumbs && breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.id}>
                <svg className="kc-breadcrumb-separator" width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                  <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 01.708 0l6 6a.5.5 0 010 .708l-6 6a.5.5 0 01-.708-.708L10.293 8 4.646 2.354a.5.5 0 010-.708z" clipRule="evenodd" />
                </svg>
                <button
                  className={`kc-breadcrumb-item ${index === breadcrumbs.length - 1 ? 'active' : ''}`}
                  onClick={() => index < breadcrumbs.length - 1 ? navigateToFolder(crumb.id) : null}
                  disabled={index === breadcrumbs.length - 1}
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : null}

      {/* Content */}
      <div className="kc-content">
        <FileExplorer
          folders={folders}
          documents={documents}
          viewMode="list"
          canManage={canManage}
          onFolderClick={(folder) => navigateToFolder(folder.id)}
          onFileClick={(doc) => {
            setContextMenu(null);
            const viewable = ['image/', 'video/', 'application/pdf'];
            if (viewable.some(t => doc.content_type?.startsWith(t)) && (doc.viewer_url || doc.direct_url)) {
              window.open(doc.viewer_url || doc.direct_url, '_blank');
            } else {
              downloadFile(doc);
            }
          }}
          onFolderContextMenu={(e, folder) => handleContextMenu(e, folder, 'folder')}
          onFileContextMenu={(e, doc) => handleContextMenu(e, doc, 'document')}
          onFolderAction={canManage ? handleFolderAction : undefined}
          onFileAction={handleFileAction}
          getFileIcon={getFileIcon}
          formatDate={formatDate}
          loading={loading}
          emptyMessage="No documents linked. Upload files or link existing documents from the Library to this project."
        />
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="library-context-menu"
          style={{ position: 'fixed', top: contextMenu.y, left: contextMenu.x, zIndex: 9999 }}
        >
          {contextMenu.item.type === 'document' && (
            <>
              <button className="context-item" onClick={() => {
                const url = contextMenu.item.viewer_url || contextMenu.item.direct_url;
                if (url) window.open(url, '_blank');
                setContextMenu(null);
              }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 011.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0114.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 011.172 8z" />
                  <path d="M8 5.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z" />
                </svg>
                Open
              </button>
              <button className="context-item" onClick={() => {
                downloadFile(contextMenu.item);
                setContextMenu(null);
              }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5z" />
                  <path d="M7.646 11.854a.5.5 0 00.708 0l3-3a.5.5 0 00-.708-.708L8.5 10.293V1.5a.5.5 0 00-1 0v8.793L5.354 8.146a.5.5 0 10-.708.708l3 3z" />
                </svg>
                Download
              </button>
            </>
          )}
          {canManage && (
            <>
              <button className="context-item" onClick={() => {
                const item = contextMenu.item;
                setRenameTarget(item);
                setRenameName(item.type === 'folder' ? item.name : item.original_filename);
                setShowRenameModal(true);
                setContextMenu(null);
              }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M12.146.146a.5.5 0 01.708 0l3 3a.5.5 0 010 .708l-10 10a.5.5 0 01-.168.11l-5 2a.5.5 0 01-.65-.65l2-5a.5.5 0 01.11-.168l10-10zM11.207 2.5L13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 01.5.5v.5h.5a.5.5 0 01.5.5v.5h.293l6.5-6.5z" />
                </svg>
                Rename
              </button>
              <div className="context-separator" />
              {contextMenu.item.type === 'document' ? (
                <>
                  <button className="context-item context-item-danger" onClick={() => handleUnlinkDocument(contextMenu.item)}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M11 2.5a2.5 2.5 0 11.603 1.628l-6.97 4.02a2.5 2.5 0 010 1.704l6.97 4.02a2.5 2.5 0 11-.56.866l-6.985-4.028a2.5 2.5 0 110-3.444L10.44 3.366A2.5 2.5 0 0111 2.5z"/>
                    </svg>
                    Unlink from project
                  </button>
                  <button className="context-item context-item-danger" onClick={() => handleDeleteDocument(contextMenu.item)}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z"/>
                      <path fillRule="evenodd" d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 01-1-1V2a1 1 0 011-1H6a1 1 0 011-1h2a1 1 0 011 1h3.5a1 1 0 011 1v1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                    </svg>
                    Delete file
                  </button>
                </>
              ) : (
                <button className="context-item context-item-danger" onClick={() => handleUnlinkFolder(contextMenu.item)}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M11 2.5a2.5 2.5 0 11.603 1.628l-6.97 4.02a2.5 2.5 0 010 1.704l6.97 4.02a2.5 2.5 0 11-.56.866l-6.985-4.028a2.5 2.5 0 110-3.444L10.44 3.366A2.5 2.5 0 0111 2.5z"/>
                  </svg>
                  Unlink from project
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Create Folder Modal */}
      <Modal isOpen={showCreateFolderModal} onClose={() => setShowCreateFolderModal(false)} title="New Folder" size="small">
        <div className="form-group">
          <label className="form-label">Folder Name</label>
          <input
            type="text"
            className="form-input"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Enter folder name"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
          />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" style={{ marginTop: 0 }} onClick={() => setShowCreateFolderModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreateFolder} disabled={!newFolderName.trim() || creatingFolder}>
            {creatingFolder ? 'Creating...' : 'Create'}
          </button>
        </div>
      </Modal>

      {/* Rename Modal */}
      <Modal isOpen={showRenameModal} onClose={() => setShowRenameModal(false)} title={`Rename ${renameTarget?.type === 'folder' ? 'Folder' : 'File'}`} size="small">
        <div className="form-group">
          <label className="form-label">New Name</label>
          <input
            type="text"
            className="form-input"
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleRename()}
          />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" style={{ marginTop: 0 }} onClick={() => setShowRenameModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleRename} disabled={!renameName.trim() || renaming}>
            {renaming ? 'Saving...' : 'Save'}
          </button>
        </div>
      </Modal>

      {/* Link Existing Modal */}
      {showLinkModal && (
        <div className="modal-backdrop" onClick={() => setShowLinkModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Link from Library</h3>
              <button className="modal-close" onClick={() => setShowLinkModal(false)}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="link-modal-search">
                <input type="text" placeholder="Search library..." className="form-input" value={linkSearch} onChange={(e) => handleLinkSearch(e.target.value)} />
              </div>
              <div className="link-modal-content">
                {folderTree.length > 0 && (
                  <div className="link-section">
                    <h4 className="link-section-title">Folders</h4>
                    {folderTree.map((folder) => (
                      <label key={`lf-${folder.id}`} className={`link-item ${linkedFolderIds.has(folder.id) ? 'already-linked' : ''}`}>
                        <input type="checkbox" checked={selectedFolderIds.includes(folder.id)} onChange={() => toggleFolderSelection(folder.id)} disabled={linkedFolderIds.has(folder.id)} />
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="#ffab00"><path d="M.54 3.87L.5 3a2 2 0 012-2h3.672a2 2 0 011.414.586l.828.828A2 2 0 009.828 3h3.982a2 2 0 011.992 2.181l-.637 7A2 2 0 0113.174 14H2.826a2 2 0 01-1.991-1.819l-.637-7a1.99 1.99 0 01.342-1.31z" /></svg>
                        <span>{folder.name}</span>
                        {linkedFolderIds.has(folder.id) && <span className="already-badge">Already linked</span>}
                      </label>
                    ))}
                  </div>
                )}
                {linkLoading ? (
                  <div className="loading-state" style={{ padding: '20px 0' }}><span className="spinner"></span></div>
                ) : (
                  <div className="link-section">
                    <h4 className="link-section-title">Documents</h4>
                    {linkDocuments.length === 0 && <p className="link-empty">No documents found</p>}
                    {linkDocuments.map((doc) => (
                      <label key={`ld-${doc.id}`} className={`link-item ${linkedDocIds.has(doc.id) ? 'already-linked' : ''}`}>
                        <input type="checkbox" checked={selectedDocIds.includes(doc.id)} onChange={() => toggleDocSelection(doc.id)} disabled={linkedDocIds.has(doc.id)} />
                        <span className="link-file-icon">{getFileIcon(doc.content_type)}</span>
                        <span className="link-item-name">{doc.original_filename}</span>
                        <span className="link-item-size">{doc.file_size_formatted}</span>
                        {linkedDocIds.has(doc.id) && <span className="already-badge">Already linked</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" style={{ marginTop: 0 }} onClick={() => setShowLinkModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleBulkLink} disabled={selectedDocIds.length === 0 && selectedFolderIds.length === 0}>
                Link {selectedDocIds.length + selectedFolderIds.length > 0 ? `(${selectedDocIds.length + selectedFolderIds.length})` : ''} Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        isLoading={confirmModal.isLoading}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default KnowledgeCenter;
